import * as React from 'react';

import type { FormattedMessage } from 'react-intl';
import { LoginAttemptErrorType } from 'rest/login/login';
import type { changeEmailInput, checkLoginAction, forgotPassword, loginAttempt, resetLogin } from 'state/actions/login';
import type { LoginUIState } from 'state/ui/login';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { getBaseName } from '@atlassian/help-center-common-util/history';
import { search } from '@atlassian/help-center-common-util/location';
import { parseLoginSignupRedirectUrlParams } from '@atlassian/help-center-common-util/url';
import { getSsoTestModeParamFromUrl } from '@atlassian/help-center-common-util/url/url';
import { LoginFormStateless } from '../login-form-stateless';
import messages from './messages';

interface LoginFormProps {
    contactAdminLink: string;
    resetUrl: string;
    loginUIState: LoginUIState;
    canResetPassword: boolean;
    // injected via hocs
    changeEmailInputAction: typeof changeEmailInput;
    checkLoginActionAction: typeof checkLoginAction;
    loginAttemptAction: typeof loginAttempt;
    forgotPasswordAction: typeof forgotPassword;
    resetLoginAction: typeof resetLogin;
    loginAnnouncementLocale?: string;
    portalId: string | undefined;
}

interface LoginFormState {
    password: string;
    validEmail: boolean;
}

// eslint-disable-next-line no-useless-escape
const EMAIL_PATTERN = /.+\@.+\..+/;

export class LoginForm extends React.Component<LoginFormProps & WithAnalyticsEventsProps, LoginFormState> {
    state: LoginFormState = {
        password: '',
        validEmail: true,
    };

    onEmailFieldChange = (email: string) => {
        this.props.changeEmailInputAction(email);
    };

    onEmailFieldFocus = () => {
        if (!this.props.loginUIState.email) {
            this.props.resetLoginAction(this.props.loginUIState.email);
        }
    };

    onPasswordChange = (password: string) => {
        this.setState({
            password,
        });
    };

    validateEmail = (): boolean => {
        const validEmail = EMAIL_PATTERN.test(this.props.loginUIState.email);
        this.setState({
            validEmail,
        });
        return validEmail;
    };

    onSubmit = () => {
        if (this.validateEmail()) {
            const { loginUIState, loginAttemptAction, checkLoginActionAction, createAnalyticsEvent } = this.props;
            const authData = this.buildAuthDataRequest();

            const { password } = this.state;
            if (loginUIState.atlassianAccountCheckPerformed) {
                loginAttemptAction(loginUIState.email.trim(), password);
            } else {
                checkLoginActionAction(loginUIState.email.trim(), this.props.portalId, authData, createAnalyticsEvent);
            }
        }
    };

    private buildAuthDataRequest() {
        const { destination, isAbsolute } = parseLoginSignupRedirectUrlParams();
        const prefix = destination && isAbsolute ? '' : `${getBaseName()}/`;
        const redirectUrl = `${prefix}${destination || this.getDefaultDestination()}`;
        const params = new URLSearchParams(search());

        return {
            ssoTest: getSsoTestModeParamFromUrl(params),
            redirectTo: redirectUrl,
            absoluteRedirectUrl: isAbsolute,
        };
    }

    getDefaultDestination = (): string => {
        const { portalId } = this.props;
        if (portalId) {
            return `portal/${portalId}`;
        }
        return 'portals';
    };

    onForgotPasswordLinkClick = () => {
        const { loginUIState, forgotPasswordAction, resetUrl } = this.props;
        forgotPasswordAction(resetUrl, loginUIState.email.trim());
    };

    getLoginError = (): FormattedMessage.MessageDescriptor | undefined => {
        const { error, loginAttemptResponse } = this.props.loginUIState;
        if (error) {
            return error.description;
        }

        const loginFailure = loginAttemptResponse?.error;
        if (loginFailure) {
            switch (loginFailure.errorType) {
                case LoginAttemptErrorType.INVALID_CREDENTIALS:
                    return messages.invalidLogin;
                case LoginAttemptErrorType.ACCOUNT_LOCKED_ERROR:
                    return messages.accountLocked;
                case LoginAttemptErrorType.MISC_LOGIN_ERROR:
                default:
                    return messages.miscLoginError;
            }
        }

        return undefined;
    };

    render() {
        const { canResetPassword, loginUIState, contactAdminLink, loginAnnouncementLocale } = this.props;
        const { validEmail, password } = this.state;

        return (
            <LoginFormStateless
                isSubmitting={loginUIState.isSubmitting}
                isSendingForgotPasswordEmail={loginUIState.isSendingForgotPasswordEmail}
                onSubmit={this.onSubmit}
                onEmailFieldChange={this.onEmailFieldChange}
                onPasswordChange={this.onPasswordChange}
                validEmail={validEmail}
                email={loginUIState.email}
                password={password}
                loginError={this.getLoginError()}
                atlassianAccountCheckPerformed={loginUIState.atlassianAccountCheckPerformed}
                loginFailure={loginUIState.loginAttemptResponse?.error}
                onEmailFieldFocus={this.onEmailFieldFocus}
                onForgotPasswordLinkClick={this.onForgotPasswordLinkClick}
                contactAdminLink={contactAdminLink}
                canResetPassword={canResetPassword}
                loginAnnouncementLocale={loginAnnouncementLocale}
            />
        );
    }
}
